import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '../utils/toast';
import { MapPin, Plus, Search, Edit, ToggleLeft, ToggleRight, Loader2, Database } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LocationManagementPage = () => {
  const navigate = useNavigate();
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [seeding, setSeeding] = useState(false);
  
  // Add/Edit modal state
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [modalData, setModalData] = useState({ city: '', state: '' });
  const [saving, setSaving] = useState(false);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data.locations || []);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/signin');
      } else {
        toast.error('Failed to load locations');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLocations();
  }, []);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/admin/locations/seed`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed locations');
    } finally {
      setSeeding(false);
    }
  };

  const handleSave = async () => {
    if (!modalData.city.trim()) {
      toast.error('City name is required');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingLocation) {
        await axios.put(`${API}/admin/locations/${editingLocation.location_id}`, modalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Location updated successfully');
      } else {
        await axios.post(`${API}/admin/locations`, modalData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Location added successfully');
      }
      setShowModal(false);
      setEditingLocation(null);
      setModalData({ city: '', state: '' });
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save location');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (location) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API}/admin/locations/${location.location_id}/toggle`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle location');
    }
  };

  const openEdit = (location) => {
    setEditingLocation(location);
    setModalData({ city: location.city, state: location.state || '' });
    setShowModal(true);
  };

  const openAdd = () => {
    setEditingLocation(null);
    setModalData({ city: '', state: '' });
    setShowModal(true);
  };

  const filteredLocations = locations.filter(loc => {
    const q = searchQuery.toLowerCase();
    return loc.city?.toLowerCase().includes(q) || loc.state?.toLowerCase().includes(q);
  });

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <MapPin className="h-6 w-6 text-blue-600" />
              Location Master
            </h1>
            <p className="text-gray-500 mt-1">Manage cities available for user registration</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSeed}
              disabled={seeding}
              variant="outline"
              className="flex items-center gap-2"
            >
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Database className="h-4 w-4" />}
              Seed Indian Cities
            </Button>
            <Button onClick={openAdd} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add Location
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Stats */}
        <div className="flex gap-4 text-sm text-gray-600">
          <span>Total: <strong>{locations.length}</strong></span>
          <span>Active: <strong>{locations.filter(l => l.is_active).length}</strong></span>
          <span>Inactive: <strong>{locations.filter(l => !l.is_active).length}</strong></span>
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-lg border overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">State</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredLocations.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                      {searchQuery ? 'No locations match your search' : 'No locations found. Click "Seed Indian Cities" to add default cities.'}
                    </td>
                  </tr>
                ) : (
                  filteredLocations.map((loc) => (
                    <tr key={loc.location_id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">{loc.city}</td>
                      <td className="px-4 py-3 text-gray-600">{loc.state || '-'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          loc.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {loc.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openEdit(loc)}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleToggle(loc)}
                            className={`p-1.5 rounded hover:bg-gray-100 ${loc.is_active ? 'text-green-600 hover:text-red-600' : 'text-red-600 hover:text-green-600'}`}
                            title={loc.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {loc.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
              <h2 className="text-lg font-semibold mb-4">
                {editingLocation ? 'Edit Location' : 'Add Location'}
              </h2>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City <span className="text-red-500">*</span></Label>
                  <Input
                    id="city"
                    value={modalData.city}
                    onChange={(e) => setModalData({ ...modalData, city: e.target.value })}
                    placeholder="Enter city name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={modalData.state}
                    onChange={(e) => setModalData({ ...modalData, state: e.target.value })}
                    placeholder="Enter state name"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => { setShowModal(false); setEditingLocation(null); }}>
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {editingLocation ? 'Update' : 'Add'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default LocationManagementPage;
